import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../services/base'
import Button from '../common/Button'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../context/global_context'
import {SELECT_WIRE, SET_WIRE_LIST, WireContext} from './context/context'
import InputText from '../common/InputText'
import WireFormModal from './form'
import WireTable from './WireTable'
import {getSessionStorageData} from '../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Space} from 'antd'
import useDebouncedValue from '../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../utils/notifications'

const WireList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(WireContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/wire/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.wires' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getWiresData()
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/wire/?vehicle=${vehicle}&search=${search}`
    getWiresData()
  }, [debouncedSearch])

  const getWiresData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'wire.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_WIRE_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'wire.loadError' }))
    }
  }

  const buildWireUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })
    return `/api/wire/?${params.toString()}`
  }

  const deleteWire = async (wire_id) => {
    let res = await deleteRequest(`/api/wire/${wire_id}/?vehicle=${vehicle}`)
    // Handle edge case of deleting the last record on the last page
    const { currentPage, totalCount, totalPages } = pagination
    const pageSize = 10
    const newTotalCount = totalCount - 1
    const newTotalPages = Math.max(1, Math.ceil(newTotalCount / pageSize))
    const pageToFetch = Math.min(currentPage, newTotalPages)
    const offset = (pageToFetch - 1) * pageSize

    getWiresData(
      buildWireUrl({
        vehicle: vehicle,
        search: search,
        limit: pageSize,
        offset: offset,
      })
    )
    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'wire.deleteSuccess' }),
      },
    })
  }

  const confirmDeleteWire = (wire_id) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'wire.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'wire.confirmDeleteContent' }),
      onOk: () => {
        deleteWire(wire_id)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const onEditWire = (record) => {
    dispatch({
      type: SELECT_WIRE,
      data: { selected_wire: record, show_wire_popup: true },
    })
  }

  const onCreateWire = () => {
    dispatch({
      type: SELECT_WIRE,
      data: { selected_wire: {}, show_wire_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.wire_list?.length) return []

    return state?.wire_list?.map((wire, index) => {
      return {
        ...wire,
        key: `wire-${index}`,
        rowKey: `wire-${wire.id}`,
        index: index,
        harness_id: wire.harness?.harness_id,
        circuit_name: wire.circuit?.name,
        connector_from_name: wire.connector_from?.name,
        connector_to_name: wire.connector_to?.name,
        pin: `${wire.pin_from || ''} / ${wire.pin_to || ''}`,
      }
    })
  }, [state.wire_list])

  return (
    <>
      {contextHolder}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_wire"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'wire.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-wire"
                onChange={(value) => {
                  setSearch(value)
                }}
              />

              <Space size="middle" align="center">
                <Button onClick={onCreateWire}>
                  {intl.formatMessage({ id: 'wire.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <WireTable
              dataSource={dataSource}
              pagination={pagination}
              getWiresData={getWiresData}
              onEdit={onEditWire}
              onDelete={confirmDeleteWire}
            />
          </Flex>
        </Row>
      </div>

      {/* Create / Edit Wire Popup */}
      {state.show_wire_popup && (
        <WireFormModal
          getListData={() => {
            getWiresData()
          }}
        />
      )}
    </>
  )
}

export default WireList
